import { Location } from '@angular/common';
import {
  type AfterViewInit,
  Component,
  ElementRef,
  inject,
  type OnDestroy,
  type OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { type NavigationExtras, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  cleanForFilter,
  sortByNameExactFirst,
  sortCaseInsensitive,
} from '../../../server/services/utils.service';
import type { OrgTag } from '../../../server/types/org-types';
import type { DeepReadonly } from '../../../server/util-type';
import { TagService } from '../tag.service';

export type FilterTaskTagDialogComponentData = {
  orgId: string;
  spaceId: string;
  tagIds: string[];
  noTags: boolean;
};

@Component({
  selector: 'app-filter-task-tag-dialog',
  templateUrl: './filter-task-tag-dialog.component.html',
  styleUrls: ['./filter-task-tag-dialog.component.scss'],
  standalone: false,
})
export class FilterTaskTagDialogComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private dialogRef = inject(MatDialogRef<FilterTaskTagDialogComponent>);
  private readonly data: FilterTaskTagDialogComponentData =
    inject(MAT_DIALOG_DATA);

  private tagService = inject(TagService);
  private router = inject(Router);
  private location = inject(Location);

  orgId: string;
  spaceId: string;
  tags: DeepReadonly<OrgTag>[] = [];
  tagIds: string[];
  tagsSelected: DeepReadonly<OrgTag>[];
  noTags = false;
  tagsRemaining: DeepReadonly<OrgTag>[] = [];

  filterControl: FormControl<string>;

  filteredTagsSelected: OrgTag[] = [];
  filteredTagsRemaining: OrgTag[] = [];

  showNoTag = true;
  noTagsText = 'Tasks with no tags';
  showClearFilter = true;
  clearFilterText = 'Clear tag filter';

  preserveTaskIdPath: string;
  removeTaskIdPath: string;

  @ViewChild('filterInput') filterInput: ElementRef;

  locationVoidFunction?: VoidFunction;

  subscriptions = new Subscription();

  constructor() {
    this.orgId = this.data.orgId;
    this.spaceId = this.data.spaceId;
    this.tagIds = this.data.tagIds;
    this.noTags = this.data.noTags;
  }

  ngOnInit(): void {
    this.filterControl = new FormControl('', { nonNullable: true });
    const { orgId, spaceId, tagIds } = this;

    const subInitial = this.tagService
      .allTags({ orgId, spaceId })
      .subscribe((tags) => {
        this.tagsSelected = [];
        this.tagsRemaining = [];
        tags.sort((a, b) => sortCaseInsensitive(a.name, b.name));
        tags.forEach((tag) => {
          if (tagIds.includes(tag._id)) this.tagsSelected.push(tag);
          else this.tagsRemaining.push(tag);
        });
        this.tags = tags;
        this.filter('');
      });
    this.subscriptions.add(subInitial);

    // We use this method because clicking on the tasks (changing the url) does
    // not trigger a pop state and hence does not trigger other location and
    // router subscriptions.
    this.locationVoidFunction = this.location.onUrlChange((val) => {
      this.preserveTaskIdPath = val.split('?')[0];
      this.removeTaskIdPath = val.split('?')[0].split('/task/')[0];
    });
    this.preserveTaskIdPath = this.location.path().split('?')[0];
    this.removeTaskIdPath = this.location
      .path()
      .split('?')[0]
      .split('/task/')[0];

    const sub = this.filterControl.valueChanges.subscribe((value) =>
      this.filter(value)
    );
    this.subscriptions.add(sub);
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.filterInput.nativeElement.focus();
    }, 200); // 200ms because the animation takes about 150ms
  }

  filter(inputValue: string) {
    const value = cleanForFilter(inputValue);
    const tags = this.tags.filter((t) =>
      cleanForFilter(t.name).includes(value)
    );

    this.filteredTagsSelected = [];
    this.filteredTagsRemaining = [];
    this.showNoTag = false;
    this.showClearFilter = false;

    if (!value) {
      this.showNoTag = true;
      this.showClearFilter = true;
    }
    if (cleanForFilter(this.noTagsText).includes(value)) this.showNoTag = true;
    if (cleanForFilter(this.clearFilterText).includes(value))
      this.showClearFilter = true;

    const selectedTagIds = this.tagsSelected.map((r) => r._id);
    const remainingTagIds = this.tagsRemaining.map((r) => r._id);

    tags.forEach((tag) => {
      if (selectedTagIds.includes(tag._id))
        return this.filteredTagsSelected.push(tag);
      if (remainingTagIds.includes(tag._id))
        return this.filteredTagsRemaining.push(tag);
    });

    this.filteredTagsSelected.sort(sortByNameExactFirst(inputValue));
    this.filteredTagsRemaining.sort(sortByNameExactFirst(inputValue));
  }

  setFilter(
    value:
      | { tagId: string; noTags?: undefined }
      | { tagId: undefined; noTags?: undefined }
      | { noTags: boolean; tagId?: undefined }
  ) {
    const navigationExtras: NavigationExtras = {
      queryParams: { tagId: null, noTags: null }, // default to clear
      queryParamsHandling: 'merge',
    };
    let path: string;
    if (value.noTags === true) {
      // Select "no tags"
      navigationExtras.queryParams = { tagId: '0' };
      path = this.removeTaskIdPath;
    } else if (value.noTags === false) {
      // Deselect "no tags"
      navigationExtras.queryParams = { tagId: null };
      path = this.preserveTaskIdPath;
    } else if (value.tagId) {
      const tagIds = this.tagsSelected.map(({ _id }) => _id);
      if (!tagIds.includes(value.tagId)) {
        // Add tag
        navigationExtras.queryParams = { tagId: [...tagIds, value.tagId] };
        path = this.removeTaskIdPath;
      } else {
        // Unselect selected tags
        navigationExtras.queryParams = {
          tagId: tagIds.filter((t) => t !== value.tagId),
        };
        path = this.preserveTaskIdPath;
      }
    } else {
      navigationExtras.queryParams = { tagId: null };
      path = this.preserveTaskIdPath;
    }

    this.router.navigate([path], navigationExtras);
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    if (this.locationVoidFunction) this.locationVoidFunction();
    this.subscriptions.unsubscribe();
  }
}
